import axios from 'axios';
import { normalizeEmail } from '@utils/normalizeEmail';

export type SetupVerifyResponse =
  | { status: 'VALID'; email: string }
  | { status: 'EXPIRED' | 'USED' | 'INVALID' };

export type SetupErrorCode =
  | 'TOKEN_EXPIRED'
  | 'TOKEN_USED'
  | 'TOKEN_INVALID'
  | 'PASSWORD_POLICY'
  | 'SIGN_IN_FAILED';

/** `{ token }` on success (same shape as signIn); `{ errorCode, errorMessage }` on a typed failure. */
export type SetupCompleteResponse = {
  token?: string;
  errorCode?: SetupErrorCode;
  errorMessage?: string;
};

export const authService = {
  signIn: async ({ user, password }: { user: string; password: string }) => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}auth/signIn`,
      {
        email: normalizeEmail(user),
        password,
      },
    );
    return response.data;
  },
  signInWithConfirm: async (
    email: string,
    password: string,
    confirmToken: string,
  ) => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}auth/signInWithConfirm`,
      {
        email: normalizeEmail(email),
        password,
        confirmToken,
      },
    );
    return response.data;
  },
  // Sprint 47, story-232. One-time account setup link. `verifySetupToken` is read-only (never
  // consumes the token), so a page load or an email-link scanner cannot spend it; only
  // `completeSetup` consumes. The password is sent exactly as typed (JSON field, no escaping).
  verifySetupToken: async (token: string): Promise<SetupVerifyResponse> => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}auth/setup/verify`,
      { token },
    );
    return response.data;
  },
  completeSetup: async (
    token: string,
    password: string,
  ): Promise<SetupCompleteResponse> => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}auth/setup/complete`,
      { token, password },
    );
    return response.data;
  },
  resendSetup: async (email: string): Promise<{ success: boolean }> => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}auth/setup/resend`,
      { email: normalizeEmail(email) },
    );
    return response.data;
  },
  forgotPassword: async (email: string) => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}auth/forgotPassword`,
      {
        email: normalizeEmail(email),
      },
    );
    return response.data;
  },
  confirmForgotPassword: async (
    email: string,
    confirmationCode: string,
    newPassword: string,
  ) => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}auth/confirmForgotPassword`,
      {
        email: normalizeEmail(email),
        confirmationCode,
        newPassword,
      },
    );
    return response.data;
  },
  register: async ({
    firstName,
    lastName,
    email,
    password,
    organizationName,
    planId,
  }: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    organizationName?: string;
    planId?: string;
  }) => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}auth/register`,
      {
        firstName,
        lastName,
        email: normalizeEmail(email),
        password,
        organizationName,
        planId,
      },
    );
    return response.data;
  },

  registerWithPayment: async (data: {
    // User info
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    organizationName: string;
    // Plan — required, there is no default plan
    planId: string;
    // Payment config
    paymentMethodType: 'credit_card';
    // Must match the backend enum exactly; it is sent through verbatim below.
    billingPeriod: 'MONTHLY' | 'ANNUAL';
    // Billing address
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
    phone?: string;
    company?: string;
    // Payment token (optional)
    paymentToken?: string;
    // Credit card fields (if not using token)
    ccNumber?: string;
    ccExp?: string;
    cvv?: string;
    // Google Pay (optional)
    googlePaymentData?: string;
  }) => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}auth/register-with-payment`,
      { ...data, email: normalizeEmail(data.email) },
    );
    return response.data;
  },
};

export default authService;
